import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

/**
 * Lightweight dialog that lets the user re-tint the chat bubbles for the
 * current thread. Separated from RenameDialog so both the chat header and the
 * message dropdown can open just the color picker without the name field.
 */
const DEFAULT_PALETTE = ["#2563EB", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444", "#06B6D4"];

export default function ColorPickerDialog({ open, onOpenChange, currentColor, threadName, onSave, onReset, colors, scope = "thread" }) {
  const palette = colors || DEFAULT_PALETTE;
  const [color, setColor] = useState(currentColor || palette[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setColor(currentColor || palette[0]);
  }, [open, currentColor]);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSave(color);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (busy || !onReset) return;
    setBusy(true);
    try {
      await onReset();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="color-picker-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Color</DialogTitle>
          {scope === "message" ? (
            <p className="text-xs text-[#9CA3AF] mt-1">Highlight just this note. Use <b>Reset color</b> to drop the override.</p>
          ) : threadName ? (
            <p className="text-xs text-[#9CA3AF] mt-1">Every bubble in <b className="text-[#F3F4F6]">{threadName}</b> picks up this color.</p>
          ) : null}
        </DialogHeader>
        <div className="space-y-4">
          <Label className="text-xs text-[#9CA3AF]">Pick a preset or a custom hex</Label>
          <div className="flex items-center gap-2 flex-wrap">
            {palette.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`bubble-color-swatch-${c}`}
                onClick={() => setColor(c)}
                className={`h-9 w-9 rounded-full transition-all ${
                  color === c
                    ? "ring-2 ring-offset-2 ring-offset-[#121212] ring-[#F3F4F6] scale-110"
                    : "hover:scale-105"
                }`}
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
            <label
              className="h-9 w-9 rounded-full relative cursor-pointer border border-[#27272A] overflow-hidden"
              title="Pick a custom color"
              style={{ background: "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)" }}
            >
              <input
                type="color"
                data-testid="bubble-color-swatch-custom"
                value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : "#2563EB"}
                onChange={(e) => setColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          </div>
          <div className="rounded-xl bg-[#0A0A0A] border border-[#1E1E1E] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-2">Preview</div>
            <div className="flex justify-end">
              <div
                data-testid="bubble-color-preview"
                className="text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm shadow-sm max-w-[75%]"
                style={{ backgroundColor: color }}
              >
                This is what your notes will look like.
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {scope === "message" && onReset && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={reset}
                  data-testid="bubble-color-reset-btn"
                  disabled={busy}
                  className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
                >
                  Reset color
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={save}
                data-testid="bubble-color-save-btn"
                disabled={busy}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                {busy ? "Saving…" : "Save color"}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
